import React, { useEffect, useRef } from 'react';
function useScrollReveal(delay = 0) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.opacity = '0';
    el.style.transform = 'translateY(40px)';
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setTimeout(() => {
          el.style.transition = 'opacity 0.8s ease, transform 0.8s ease';
          el.style.opacity = '1';
          el.style.transform = 'translateY(0)';
        }, delay);
        observer.disconnect();
      }
    }, {
      threshold: 0.1
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [delay]);
  return ref;
}
export function RealLifeSection() {
  const headerRef = useScrollReveal(0);
  const gridRef = useScrollReveal(200);
  const closingRef = useScrollReveal(400);
  return <section className="w-full py-20 sm:py-28" style={{
    backgroundColor: '#0f0f0f'
  }} aria-label="Built for real life in Hialeah" data-id="element-422">
      <div className="max-w-4xl mx-auto px-4 sm:px-6" data-id="element-423">
        <div ref={headerRef} data-id="element-424">
          <div className="flex items-center gap-3 mb-6" data-id="element-425">
            <div style={{
            width: '40px',
            height: '1px',
            background: '#C9A84C'
          }} data-id="element-426" />
            <span className="section-label" data-id="element-427">Built for Real Life</span>
          </div>
          <h2 style={{
          fontFamily: 'var(--font-heading)',
          fontSize: 'clamp(1.8rem, 4vw, 2.8rem)',
          fontWeight: 700,
          color: '#F5F5F5',
          lineHeight: 1.2,
          marginBottom: '1rem'
        }} data-id="element-428">
            Built for Real Life{' '}
            <span style={{
            color: '#C9A84C',
            fontStyle: 'italic'
          }} data-id="element-429">
              in Hialeah
            </span>
          </h2>
          <p style={{
          fontFamily: 'var(--font-body)',
          fontSize: '1.05rem',
          color: '#888',
          lineHeight: 1.7,
          marginBottom: '3rem'
        }} data-id="element-430">
            We know how life works here. You've got work. You've got family.
            You've got somewhere to be.
          </p>
        </div>

        <div ref={gridRef} className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-431">
          <LifeCard icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-433">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" data-id="element-434" />
                <circle cx="9" cy="7" r="4" data-id="element-435" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" data-id="element-436" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" data-id="element-437" />
              </svg>} title="Walk-Ins Always Welcome" description="Come when you can, not when an app tells you to. No scheduling, no waiting weeks." data-id="element-432" />
          <LifeCard icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-439">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" data-id="element-440" />
                <polyline points="9 22 9 12 15 12 15 22" data-id="element-441" />
              </svg>} title="Clean & Welcoming Shop" description="You'll feel it when you walk in. A space that respects you and your time." data-id="element-438" />
          <LifeCard icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-443">
                <circle cx="12" cy="8" r="4" data-id="element-444" />
                <path d="M6 20v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2" data-id="element-445" />
                <path d="M12 12v4" data-id="element-446" />
              </svg>} title="Kids Welcome" description="Bring your son, your nephew, your little brother. Patient, skilled, kid-friendly every time." data-id="element-442" />
          <LifeCard icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-448">
                <circle cx="12" cy="12" r="10" data-id="element-449" />
                <polyline points="12 6 12 12 16 14" data-id="element-450" />
              </svg>} title="Open Until 8PM" description="For the guys who work all day and still need to look sharp. No rushing. No cutting corners." data-id="element-447" />
        </div>

        <div ref={closingRef} className="mt-12 text-center" data-id="element-451">
          <p style={{
          fontFamily: 'var(--font-callout)',
          fontSize: 'clamp(1.1rem, 2.5vw, 1.4rem)',
          fontWeight: 600,
          color: '#F5F5F5',
          letterSpacing: '0.03em',
          lineHeight: 1.6
        }} data-id="element-452">
            No rushing. No cutting corners.{' '}
            <span style={{
            color: '#C9A84C'
          }} data-id="element-453">
              Just real service that fits your real life.
            </span>
          </p>
        </div>
      </div>
    </section>;
}
function LifeCard({
  icon,
  title,
  description
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return <div className="flex gap-5 p-6 rounded-sm" style={{
    backgroundColor: '#111111',
    border: '1px solid rgba(201,168,76,0.15)'
  }} data-id="element-454">
      <div className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center" style={{
      backgroundColor: 'rgba(201,168,76,0.1)',
      border: '1px solid rgba(201,168,76,0.25)'
    }} data-id="element-455">
        {icon}
      </div>
      <div data-id="element-456">
        <h3 style={{
        fontFamily: 'var(--font-callout)',
        fontSize: '1.05rem',
        fontWeight: 600,
        color: '#F5F5F5',
        letterSpacing: '0.04em',
        marginBottom: '0.4rem'
      }} data-id="element-457">
          {title}
        </h3>
        <p style={{
        fontFamily: 'var(--font-body)',
        fontSize: '0.9rem',
        color: '#888',
        lineHeight: 1.6
      }} data-id="element-458">
          {description}
        </p>
      </div>
    </div>;
}